import React, { useState, useEffect } from 'react';
import './Contact.css';

import { Heading, PageTitle } from '../../components';
import { contactContent } from '../../constants';

import { FaComment, FaEnvelope, FaPhoneAlt, FaUserAlt } from 'react-icons/fa';
import axios from "axios";

import { useTranslation } from 'react-i18next';

const API_PATH = 'https://www.dworimielno.com/api/phpmailer/submit.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const Contact = () => {

  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    message: ''
  });
  
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault(); 
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];

      if (!formData.name.trim()) validationErrors.push(t('contact.errors.nameRequired', 'Imię i nazwisko jest wymagane'));
      if (!formData.email.trim()) {
        validationErrors.push(t('contact.errors.emailRequired', 'Adres email jest wymagany'));
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push(t('contact.errors.emailInvalid', 'Błędny format adresu email'));
      }
      if (!formData.number.trim()) {
        validationErrors.push(t('contact.errors.phoneRequired', 'Numer telefonu jest wymagany'));
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push(t('contact.errors.phoneInvalid', 'Błędny format numeru telefonu'));
      }
      if (!formData.message.trim()) validationErrors.push(t('contact.errors.messageRequired', 'Wymagana jest treść wiadomości'));

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | ')); 
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || t('contact.errors.sendError', 'Błąd wysłania wiadomości'));
      }

      setMailSent(true);
      setFormData({ name: '', email: '', number: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('contact.errors.generalError', 'Pojawił się problem'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
          <PageTitle 
            title={t('contact.pageTitle', 'Kontakt')} 
            page={t('contact.page', 'Kontakt')} 
            heading={t('contact.heading', 'Ogród Dwór Imielno')} 
            subTitle={t('contact.subTitle', 'Centrum kultury Obszarów wiejskich i edukacji ekologicznej oraz zachowania różnorodności biologicznej')} 
          />
    
          <section className='contact' id='contact'>
        
        <div className='contact-info'>
          {contactContent.map((contact) => (
            <div className='info-item' key={contact.id}>
              <div className='icon'>{contact.icon}</div>
              <div className="content">
                <h3>{t(contact.title)}</h3>
                {contact.content.map((value, index) => (
                  <p key={index}>{value}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className='box-container'>
          
          <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2438.5726906292775!2d19.21892617623305!3d52.32375597201041!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x471b77f2c1d0c527%3A0xca494e9f453f96d3!2sFundacja%20Dw%C3%B3r%20Imielno%20-%20Centrum%20Kultury%20Obszar%C3%B3w%20Wiejskich%2C%20Edukacji%20Ekologicznej%20i%20Zachowania%20R%C3%B3%C5%BCnorodno%C5%9Bci%20Biologicznej!5e0!3m2!1spl!2spl!4v1775915351663!5m2!1spl!2spl" 
          allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
          

          <form className='dark-form' onSubmit={handleSubmitForm}>

            <Heading 
              title={t('contact.formTitle', 'Wyślij nam wiadomość')} 
              subTitle={t('contact.formSubTitle', 'Skontaktuj się z nami')} 
              sideHeading linear 
            />

            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="name"><FaUserAlt /></label>
                <input
                  type="text"
                  className='box'
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('contact.placeholders.name', 'Imię i nazwisko')}
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  className='box'
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('contact.placeholders.email', 'Email')}
                  disabled={isSubmitting}
                  required 
                />
              </div>
            </div>

            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="number"><FaPhoneAlt /></label>
                <input
                  type="tel"
                  className='box'
                  id="number"
                  name="number"
                  value={formData.number}
                  onChange={handleInputChange}
                  placeholder={t('contact.placeholders.phone', 'Numer telefonu')}
                  disabled={isSubmitting}
                  required 
                />
              </div>
            </div>

            <div className='input-box'>
              <div className='input-field text-area'>
                <label htmlFor="message"><FaComment /></label>
                <textarea
                  cols="30"
                  rows="10"
                  id="message"
                  className='box'
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t('contact.placeholders.message', 'Wiadomość')}
                  disabled={isSubmitting}
                  required 
                ></textarea>
              </div>
            </div>

            <button type="submit" className='btn' disabled={isSubmitting}>
              {isSubmitting ? t('contact.submitting', 'Wysyłanie...') : t('contact.submit', 'Wyślij')}
            </button>

            {error && <div className="alert error">{error.split(' | ').map((msg, i) => (<span key={i}>{msg}</span>))}</div>}
            {mailSent && <div className="alert success"><span>{t('contact.success', 'Wiadomość wysłana')}</span></div>}
          </form>
        </div>
      </section>
    </>
  );
};

export default Contact;